import { useEffect, useState } from 'react';
import { dailyReport, monthlyReport } from '@/lib/api';
import { formatTRY, formatDate, todayISO, shiftDate, SERVICE_LABELS, PAYMENT_LABELS } from '@/lib/commission';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, CalendarDays } from 'lucide-react';

const TR_MONTHS = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

function KpiCard({ label, value, testId }) {
  return (
    <Card className="rounded-2xl border-border shadow-soft hover-lift" data-testid={testId}>
      <CardContent className="p-6">
        <div className="text-xs uppercase tracking-widest text-salon-muted">{label}</div>
        <div className="number-display mt-2 text-3xl text-salon-ink md:text-4xl">{value}</div>
      </CardContent>
    </Card>
  );
}

function DailyReport() {
  const [date, setDate] = useState(todayISO());
  const [data, setData] = useState(null);

  useEffect(() => {
    dailyReport(date).then(setData).catch(() => setData(null));
  }, [date]);

  return (
    <div className="space-y-6" data-testid="daily-report">
      <Card className="rounded-2xl border-border shadow-soft">
        <CardContent className="flex flex-col items-center justify-between gap-4 p-5 md:flex-row">
          <Button
            variant="outline" className="rounded-full"
            data-testid="prev-day-btn"
            onClick={() => setDate(shiftDate(date, -1))}
          >
            <ChevronLeft size={16} className="mr-1" /> Önceki Gün
          </Button>
          <div className="flex items-center gap-3">
            <CalendarDays size={18} className="text-salon-primary" />
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="rounded-xl border border-border bg-white px-4 py-2 text-center font-display text-xl focus:outline-none focus:ring-2 focus:ring-salon-primary/40"
              data-testid="daily-date-picker"
            />
          </div>
          <Button
            variant="outline" className="rounded-full"
            data-testid="next-day-btn"
            onClick={() => setDate(shiftDate(date, 1))}
          >
            Sonraki Gün <ChevronRight size={16} className="ml-1" />
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <KpiCard testId="daily-kpi-count" label="İşlem Sayısı" value={data?.transaction_count ?? 0} />
        <KpiCard testId="daily-kpi-total" label="Toplam Ciro" value={formatTRY(data?.total_amount)} />
        <KpiCard testId="daily-kpi-staff" label="Personel Payı" value={formatTRY(data?.total_staff_share)} />
        <KpiCard testId="daily-kpi-center" label="Merkez Payı" value={formatTRY(data?.total_center_share)} />
      </div>

      <Card className="rounded-2xl border-border shadow-soft">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-6 text-xs uppercase tracking-widest text-salon-muted">Müşteri</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-salon-muted">İşlem</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-salon-muted">Ödeme</TableHead>
                  <TableHead className="text-right text-xs uppercase tracking-widest text-salon-muted">Tutar</TableHead>
                  <TableHead className="text-right text-xs uppercase tracking-widest text-salon-muted">Personel</TableHead>
                  <TableHead className="pr-6 text-right text-xs uppercase tracking-widest text-salon-muted">Merkez</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!data || data.transactions.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="py-10 text-center text-sm text-salon-muted">Bu tarihte işlem bulunmuyor.</TableCell></TableRow>
                ) : data.transactions.map((t) => (
                  <TableRow key={t.id} className="border-border">
                    <TableCell className="pl-6 py-4 font-medium">{t.customer_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="rounded-full border-salon-primary/30 bg-salon-secondary/40 text-salon-primary-hover">
                        {SERVICE_LABELS[t.service_type]}
                      </Badge>
                    </TableCell>
                    <TableCell>{PAYMENT_LABELS[t.payment_method]}</TableCell>
                    <TableCell className="text-right number-display">{formatTRY(t.amount)}</TableCell>
                    <TableCell className="text-right number-display text-salon-primary-hover">{formatTRY(t.staff_share)}</TableCell>
                    <TableCell className="pr-6 text-right number-display">{formatTRY(t.center_share)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function MonthlyReport() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [data, setData] = useState(null);

  useEffect(() => {
    monthlyReport(year, month).then(setData).catch(() => setData(null));
  }, [year, month]);

  const shift = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  return (
    <div className="space-y-6" data-testid="monthly-report">
      <Card className="rounded-2xl border-border shadow-soft">
        <CardContent className="flex flex-col items-center justify-between gap-4 p-5 md:flex-row">
          <Button variant="outline" className="rounded-full" data-testid="prev-month-btn" onClick={() => shift(-1)}>
            <ChevronLeft size={16} className="mr-1" /> Önceki Ay
          </Button>
          <div className="font-display text-2xl text-salon-ink" data-testid="current-month-label">
            {TR_MONTHS[month - 1]} {year}
          </div>
          <Button variant="outline" className="rounded-full" data-testid="next-month-btn" onClick={() => shift(1)}>
            Sonraki Ay <ChevronRight size={16} className="ml-1" />
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <KpiCard testId="monthly-kpi-count" label="İşlem Sayısı" value={data?.transaction_count ?? 0} />
        <KpiCard testId="monthly-kpi-total" label="Toplam Ciro" value={formatTRY(data?.total_amount)} />
        <KpiCard testId="monthly-kpi-staff" label="Personel Payı" value={formatTRY(data?.total_staff_share)} />
        <KpiCard testId="monthly-kpi-center" label="Merkez Payı" value={formatTRY(data?.total_center_share)} />
      </div>

      <Card className="rounded-2xl border-border shadow-soft">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-6 text-xs uppercase tracking-widest text-salon-muted">Tarih</TableHead>
                  <TableHead className="text-right text-xs uppercase tracking-widest text-salon-muted">İşlem</TableHead>
                  <TableHead className="text-right text-xs uppercase tracking-widest text-salon-muted">Ciro</TableHead>
                  <TableHead className="text-right text-xs uppercase tracking-widest text-salon-muted">Personel</TableHead>
                  <TableHead className="pr-6 text-right text-xs uppercase tracking-widest text-salon-muted">Merkez</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!data ? (
                  <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-salon-muted">Yükleniyor...</TableCell></TableRow>
                ) : data.daily_summaries.filter(d => d.transaction_count > 0).length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-salon-muted">Bu ayda işlem bulunmuyor.</TableCell></TableRow>
                ) : data.daily_summaries.filter(d => d.transaction_count > 0).map((d) => (
                  <TableRow key={d.date} className="border-border">
                    <TableCell className="pl-6 py-4">{formatDate(d.date)}</TableCell>
                    <TableCell className="text-right">{d.transaction_count}</TableCell>
                    <TableCell className="text-right number-display">{formatTRY(d.total_amount)}</TableCell>
                    <TableCell className="text-right number-display text-salon-primary-hover">{formatTRY(d.total_staff_share)}</TableCell>
                    <TableCell className="pr-6 text-right number-display">{formatTRY(d.total_center_share)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function Reports() {
  return (
    <div className="space-y-8" data-testid="reports-page">
      <header className="flex flex-col gap-2">
        <div className="text-xs uppercase tracking-widest text-salon-primary">Analiz</div>
        <h1 className="font-display text-4xl text-salon-ink md:text-5xl">Raporlar</h1>
        <p className="text-sm text-salon-muted">Günlük ve aylık kazanç raporlarını görüntüleyin.</p>
      </header>

      <Tabs defaultValue="daily" className="w-full">
        <TabsList className="rounded-full bg-white p-1 shadow-soft">
          <TabsTrigger
            value="daily"
            data-testid="tab-daily"
            className="rounded-full px-6 data-[state=active]:bg-salon-primary data-[state=active]:text-white"
          >
            Günlük
          </TabsTrigger>
          <TabsTrigger
            value="monthly"
            data-testid="tab-monthly"
            className="rounded-full px-6 data-[state=active]:bg-salon-primary data-[state=active]:text-white"
          >
            Aylık
          </TabsTrigger>
        </TabsList>
        <TabsContent value="daily" className="mt-6"><DailyReport /></TabsContent>
        <TabsContent value="monthly" className="mt-6"><MonthlyReport /></TabsContent>
      </Tabs>
    </div>
  );
}
